import { useState } from "react";
import type { AllowListRow } from "@shared/schemas/admin";
import type { AppRoleName } from "@shared/schemas/auth";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { TextAreaField } from "@client/components/Field";
import { Modal } from "@client/components/Modal";
import {
  useAddAllowListEntry,
  useRevokeAllowListEntry,
  useUpdateAllowListEntry,
} from "@client/hooks/useAdmin";
import { formatDate, formatRelative } from "@client/lib/datetime";
import { ROLE_HINT, ROLE_LABEL, ROLE_ORDER } from "@client/lib/roles";
import { describeError } from "@client/lib/errorMessage";

/**
 * Who can sign in, and as what.
 *
 * The table edits in place — a role is a select, not a dialog — because changing
 * somebody's role is routine and reversible. Revoking is neither, so it is the
 * one action that stops and says what it is about to do.
 */
export function AllowListTable({
  rows,
  currentEmail,
}: {
  rows: AllowListRow[];
  currentEmail: string;
}) {
  const update = useUpdateAllowListEntry();
  const revoke = useRevokeAllowListEntry();
  const restore = useAddAllowListEntry();

  const [pendingRevoke, setPendingRevoke] = useState<AllowListRow | null>(null);
  const [editingNote, setEditingNote] = useState<AllowListRow | null>(null);
  const [noteDraft, setNoteDraft] = useState("");

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-ink-soft">
              <th scope="col" className="px-3 py-2 font-semibold">
                Email
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Role
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Invited by
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Last sign-in
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Status
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const revoked = row.revokedAt !== null;
              const isSelf = row.email === currentEmail;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-line align-top ${revoked ? "opacity-60" : ""}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-ink">{row.email}</span>
                      {row.note && <span className="text-[11px] text-ink-soft">{row.note}</span>}
                      {isSelf && <span className="text-[11px] text-ink-soft">This is you.</span>}
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    <label className="sr-only" htmlFor={`role-${row.id}`}>
                      Role for {row.email}
                    </label>
                    <select
                      id={`role-${row.id}`}
                      value={row.role}
                      disabled={revoked || update.isPending}
                      title={ROLE_HINT[row.role]}
                      onChange={(event) =>
                        update.mutate({ id: row.id, role: event.target.value as AppRoleName })
                      }
                      className="rounded-md border border-line bg-chip-bg px-2 py-1 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {ROLE_ORDER.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABEL[role]}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="px-3 py-2 text-xs text-ink-soft">
                    <div className="flex flex-col gap-0.5">
                      <span>{row.invitedBy?.fullName ?? "—"}</span>
                      <span>{formatDate(row.createdAt)}</span>
                    </div>
                  </td>

                  <td className="px-3 py-2 text-xs text-ink-soft">
                    {row.lastLoginAt ? (
                      formatRelative(row.lastLoginAt)
                    ) : (
                      <span title="They have been invited but have never signed in.">
                        Never signed in
                      </span>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    {revoked ? (
                      <Badge kind="critical" title={`Revoked ${formatDate(row.revokedAt!)}`}>
                        Revoked
                      </Badge>
                    ) : (
                      <Badge kind="positive">Active</Badge>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingNote(row);
                          setNoteDraft(row.note ?? "");
                        }}
                      >
                        Note
                      </Button>
                      {revoked ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={restore.isPending}
                          onClick={() =>
                            restore.mutate({
                              email: row.email,
                              role: row.role,
                              note: row.note,
                            })
                          }
                        >
                          Restore
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          // The server refuses this too. Hiding it as well means
                          // nobody has to discover the rule by tripping over it.
                          disabled={isSelf || revoke.isPending}
                          title={isSelf ? "You cannot revoke your own access." : undefined}
                          onClick={() => setPendingRevoke(row)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(update.isError || restore.isError) && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(update.error ?? restore.error)}
        </p>
      )}

      <ConfirmDialog
        open={pendingRevoke !== null}
        title="Revoke access?"
        body={
          <>
            {pendingRevoke?.email} is signed out immediately and cannot sign in again. Their
            scenarios are kept, and you can restore access at any time.
          </>
        }
        confirmLabel="Revoke access"
        tone="danger"
        busy={revoke.isPending}
        error={revoke.isError ? describeError(revoke.error) : null}
        onCancel={() => setPendingRevoke(null)}
        onConfirm={() => {
          if (!pendingRevoke) return;
          revoke.mutate(pendingRevoke.id, { onSuccess: () => setPendingRevoke(null) });
        }}
      />

      <Modal
        open={editingNote !== null}
        title="Why does this person have access?"
        description={editingNote?.email}
        size="sm"
        onClose={() => setEditingNote(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditingNote(null)}>
              Cancel
            </Button>
            <Button
              disabled={update.isPending}
              onClick={() => {
                if (!editingNote) return;
                update.mutate(
                  { id: editingNote.id, note: noteDraft.trim() || null },
                  { onSuccess: () => setEditingNote(null) },
                );
              }}
            >
              {update.isPending ? "Saving…" : "Save note"}
            </Button>
          </>
        }
      >
        <TextAreaField
          label="Note"
          rows={3}
          hint="Their team, the project, or who asked for them — whatever you would want to read a year from now."
          value={noteDraft}
          onChange={(event) => setNoteDraft(event.target.value)}
        />
      </Modal>
    </>
  );
}
